import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { readScheduleRows } from '../../realtime/useScheduleRealtime';
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Spinner, Alert, Form, Col, Row, Tabs, Tab, Button, Modal } from "react-bootstrap";
import { selectApiUrls, resetApiUrls } from "../../redux/slices/apiUrlSlice";
import { clearApiStatus, updateApiStatus } from "../../redux/slices/apiStatusSlice";
import { fetchDropdownValues } from "../../redux/slices/dropdownSlice";
import { fetchData, patchData } from '../../redux/slices/apiCallSlice';
// Import centralized date utilities
import { formatDateForForm, formatDateLocalEnhanced } from "../../utils/dateUtils";
import { getProjectStatus } from '../../utils/dataNormalization';

const unwrapJobRows = (response) => {
  const raw = response?.data || response || [];
  const rows = Array.isArray(raw) ? raw : Object.values(raw);

  return rows
    .map((item) => item?.GeneralProjectInfo || item)
    .filter(Boolean);
};

const normalizeOtdJob = (job) => ({
  ...job,
  ID: job.ID ?? job.id,
  JobNumber: job.JobNumber ?? job.jobNumber,
  ProjectName: job.ProjectName ?? job.projectName,
  ModelNumber: job.ModelNumber ?? job.modelNumber,
  ProjectStatus: job.ProjectStatus ?? job.projectStatus,
  JobStatus: job.JobStatus ?? job.jobStatus,
  ActualShipDate: job.ActualShipDate ?? job.actualShipDate,
  OriginalScheduledShipDate: job.OriginalScheduledShipDate ?? job.originalScheduledShipDate,
  CurrentPlanShipDate: job.CurrentPlanShipDate ?? job.currentPlanShipDate,
  ControlPanelExpectedCompletion: job.ControlPanelExpectedCompletion ?? job.controlPanelExpectedCompletion,
  PackDue: job.PackDue ?? job.packDue,
  ReasonLate: job.ReasonLate ?? job.reasonLate ?? '',
  OTDNotes: job.OTDNotes ?? job.otdNotes ?? '',
  OTDDaysLate: job.OTDDaysLate ?? job.otdDaysLate,
  OTDOverrideReason: job.OTDOverrideReason ?? job.otdOverrideReason,
  HideFromOTD: job.HideFromOTD ?? job.hideFromOTD,
});

function OnTimeDelivery() {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);  const [formData, setFormData] = useState({
    ReasonLate: '',
    OTDNotes: '',
    OTDDaysLate: '',
    OTDOverrideReason: '',
    OTDOverrideReasonOther: '',
    HideFromOTD: false
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);  const [saveSuccess, setSaveSuccess] = useState(false);
  // Helper function to format dates for input fields (replaces multiple local functions)
  const formatDateForInputLocal = (date) => formatDateForForm(date);

  useEffect(() => {
    dispatch(resetApiUrls());
    dispatch(clearApiStatus());
  }, [dispatch]);

  // Fetch dropdown values
  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  useEffect(() => {
    dispatch(fetchDropdownValues());
  }, [dispatch]);

  /****************Add dropdown options here*************************/
  const reasonLateOptions =
    options?.filter((option) => option.DropdownType === "OTD Options")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const overrideReasonOptions =
    options?.filter((option) => option.DropdownType === "OTD Override Reason")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  /***************************************************************** */

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'ActualShipDate', direction: 'ascending' });
  
  // Date range state
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const lastSaturday = new Date(today);    lastSaturday.setDate(today.getDate() - (today.getDay() + 1));
    const twoSundaysAgo = new Date(today);
    twoSundaysAgo.setDate(today.getDate() - (today.getDay() + 7));
    return {
      startDate: formatDateForInputLocal(twoSundaysAgo),
      endDate: formatDateForInputLocal(lastSaturday)
    };
  });
  
  useGuardedRealtimeList({ apiKey: 'generalProject', blocked: loading || showModal || saving,
    params: { actualShipDateFrom: dateRange.startDate, actualShipDateTo: dateRange.endDate },
    decode: payload => readScheduleRows(payload).rows,
    apply: rows => setJobs(rows.map(normalizeOtdJob).filter(job => getProjectStatus(job) === 'Shipped' && job.ActualShipDate != null && job.ActualShipDate !== '')),
  });

  const getTwoWeeksAgoRange = () => {
    const today = new Date();
    const twoWeeksAgoSunday = new Date(today);
    twoWeeksAgoSunday.setDate(today.getDate() - (today.getDay() + 14));
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - (today.getDay() + 7));
    const lastSaturday = new Date(today);    lastSaturday.setDate(today.getDate() - (today.getDay() + 1));
    return {
      startDate: formatDateForInputLocal(twoWeeksAgoSunday),
      endDate: formatDateForInputLocal(lastSunday)
    };
  };
    const getLastWeekRange = () => {
    const today = new Date();
    const lastSaturday = new Date(today);
    lastSaturday.setDate(today.getDate() - (today.getDay() + 1));
    const twoSundaysAgo = new Date(today);
    twoSundaysAgo.setDate(today.getDate() - (today.getDay() + 7));
    return {
      startDate: formatDateForInputLocal(twoSundaysAgo),
      endDate: formatDateForInputLocal(lastSaturday)
    };
  };
    const getCurrentWeekRange = () => {
    const today = new Date();
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - today.getDay());
    const nextSaturday = new Date(lastSunday);
    nextSaturday.setDate(lastSunday.getDate() + 6);
    return {
      startDate: formatDateForInputLocal(lastSunday),
      endDate: formatDateForInputLocal(nextSaturday)
    };
  };
    const getLast30DaysRange = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return {
      startDate: formatDateForInputLocal(thirtyDaysAgo),
      endDate: formatDateForInputLocal(today)
    };
  };
  
  const getAllTimeRange = () => {
    return {
      startDate: '',
      endDate: ''
    };
  };

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      dispatch(updateApiStatus({ tableName: 'generalProject', status: 'pending' }));
      const dateParams = new URLSearchParams();

      if (dateRange.startDate) {
        dateParams.set('actualShipDateFrom', dateRange.startDate);
      }

      if (dateRange.endDate) {
        dateParams.set('actualShipDateTo', dateRange.endDate);
      }

      const queryString = dateParams.toString();
      const jobsUrl = queryString
        ? `${apiUrls.generalProject}${apiUrls.generalProject.includes('?') ? '&' : '?'}${queryString}`
        : apiUrls.generalProject;
      const jobsResponse = await dispatch(fetchData({ url: jobsUrl })).unwrap();
      const jobsData = unwrapJobRows(jobsResponse).map(normalizeOtdJob);

      if (jobsData.length === 0) {
        setError("No jobs found");
        dispatch(updateApiStatus({ tableName: 'generalProject', status: 'no data' }));
      } else {
        // OTD only applies to shipped jobs; keep unshipped jobs out of this table.
        const shippedJobs = jobsData.filter((job) =>
          getProjectStatus(job) === 'Shipped' &&
          job?.ActualShipDate !== null &&
          job?.ActualShipDate !== undefined &&
          job?.ActualShipDate !== ''
        );
        setJobs(shippedJobs);
        dispatch(updateApiStatus({ tableName: 'generalProject', status: 'success' }));
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Failed to fetch jobs");
      dispatch(updateApiStatus({ tableName: 'generalProject', status: 'failed', error: err.message }));
    } finally {
      setLoading(false);
    }
  }, [apiUrls.generalProject, dateRange.endDate, dateRange.startDate, dispatch]);

  useEffect(() => {
    if (apiUrls.generalProject) {
      fetchJobs();
    }
  }, [fetchJobs, apiUrls.generalProject]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const setDateRangePreset = (presetFunction) => {
    setDateRange(presetFunction());
  };

  const sortedJobs = [...jobs].sort((a, b) => {
    if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
    if (!a[sortConfig.key]) return 1;
    if (!b[sortConfig.key]) return -1;
    
    if (sortConfig.key === 'DaysLate') {
      const aValue = calculateDaysLate(a);
      const bValue = calculateDaysLate(b);
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    } else {
      let aValue, bValue;
      
      try {
        aValue = new Date(a[sortConfig.key]);
        if (isNaN(aValue.getTime())) aValue = null;
      } catch (e) {
        aValue = null;
      }
      
      try {
        bValue = new Date(b[sortConfig.key]);
        if (isNaN(bValue.getTime())) bValue = null;
      } catch (e) {
        bValue = null;
      }
      
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    }
  });

  const filteredJobs = sortedJobs.filter((job) => {
    if (!job.ActualShipDate) return false;    const searchFilter = searchTerm
      ? (job.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         job.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         job.ModelNumber?.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;
    
    let dateFilter = true;
    if (dateRange.startDate && dateRange.endDate) {
      let shipDate;
      try {
        shipDate = new Date(job.ActualShipDate);
        if (isNaN(shipDate.getTime())) return false;
      } catch (e) {
        return false;
      }
      
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      dateFilter = shipDate >= startDate && shipDate <= endDate;
    } else if (dateRange.startDate) {
      let shipDate;
      try {
        shipDate = new Date(job.ActualShipDate);
        if (isNaN(shipDate.getTime())) return false;
      } catch (e) {
        return false;
      }
      
      const startDate = new Date(dateRange.startDate);
      dateFilter = shipDate >= startDate;
    } else if (dateRange.endDate) {
      let shipDate;
      try {
        shipDate = new Date(job.ActualShipDate);
        if (isNaN(shipDate.getTime())) return false;
      } catch (e) {
        return false;
      }
      
      const endDate = new Date(dateRange.endDate);
      endDate.setHours(23, 59, 59, 999);
      dateFilter = shipDate <= endDate;
    }
    
    return searchFilter && dateFilter;
  });

  const stationJobs = filteredJobs.filter(job => 
    job.JobNumber?.toUpperCase().startsWith('20') ||
    job.JobNumber?.toUpperCase().startsWith('99') ||
    job.JobNumber?.toUpperCase().startsWith('PLS')
  );
  const moJobs = filteredJobs.filter(job => 
    job.JobNumber?.toUpperCase().startsWith('MO'));
  const otherJobs = filteredJobs.filter(job => {
    const jobNumber = (job.JobNumber || '').toUpperCase();
    return !jobNumber.startsWith('20') &&
      !jobNumber.startsWith('99') &&
      !jobNumber.startsWith('PLS') &&
      !jobNumber.startsWith('MO');
  });
  const formatDate = (dateString) => {
    return formatDateLocalEnhanced(dateString);
  };

  const calculateDaysLate = (job) => {
    // Use OTDDaysLate from API if available
    if (job.OTDDaysLate !== null && job.OTDDaysLate !== undefined) {
      return job.OTDDaysLate;
    }
    
    // Otherwise calculate it
    if (!job.OriginalScheduledShipDate || !job.ActualShipDate) return null;
    
    try {
      const originalDate = new Date(job.OriginalScheduledShipDate);
      const actualDate = new Date(job.ActualShipDate);
      
      if (isNaN(originalDate.getTime()) || isNaN(actualDate.getTime())) {
        return null;
      }
      
      // Calculate difference in days (can be negative for early shipments)
      const diffTime = actualDate - originalDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays; // Return actual days difference (can be negative)
    } catch (e) {
      return null;
    }
  };

  // Add a new function to calculate the actual days late without override
  const calculateActualDaysLate = (job) => {
    if (!job.OriginalScheduledShipDate || !job.ActualShipDate) return null;
    
    try {
      const originalDate = new Date(job.OriginalScheduledShipDate);
      const actualDate = new Date(job.ActualShipDate);
      
      if (isNaN(originalDate.getTime()) || isNaN(actualDate.getTime())) {
        return null;
      }
      
      // Calculate difference in days (can be negative for early shipments)
      const diffTime = actualDate - originalDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays; // Return actual days difference (can be negative)
    } catch (e) {
      return null;
    }
  };

  const handleRowClick = (job) => {
    setSelectedJob(job);
    
    // Parse the OTDOverrideReason if it starts with "Other - "
    let overrideReason = job.OTDOverrideReason || '';
    let overrideReasonOther = '';
    
    if (overrideReason.startsWith('Other - ')) {
      overrideReasonOther = overrideReason.substring(8); // Remove "Other - " prefix
      overrideReason = 'Other';
    }
    
    setFormData({
      ReasonLate: job.ReasonLate || '',
      OTDNotes: job.OTDNotes || '',
      OTDDaysLate: job.OTDDaysLate !== null && job.OTDDaysLate !== undefined ? job.OTDDaysLate.toString() : '',
      OTDOverrideReason: overrideReason,
      OTDOverrideReasonOther: overrideReasonOther,
      HideFromOTD: job.HideFromOTD || false
    });
    setShowModal(true);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveChanges = async () => {
    if (!selectedJob) return;
    
    // Validation: If OTDDaysLate is overridden, require override reason
    if (formData.OTDDaysLate !== '' && !formData.OTDOverrideReason) {
      setSaveError("Override Reason is required when Days Late is overridden.");
      return;
    }
    
    // Additional validation for "Other" reason
    if (formData.OTDOverrideReason === 'Other' && !formData.OTDOverrideReasonOther) {
      setSaveError("Please specify the other reason for override.");
      return;
    }
    
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {      // Build patch object with only changed fields
      const patchPayload = {
        ID: selectedJob.ID  // Always include ID in the patch body
      };
      
      // Check each field and only include if changed
      if (formData.ReasonLate !== (selectedJob.ReasonLate || '')) {
        patchPayload.ReasonLate = formData.ReasonLate;
      }
      
      if (formData.OTDNotes !== (selectedJob.OTDNotes || '')) {
        patchPayload.OTDNotes = formData.OTDNotes;
      }
      
      // Handle OTDDaysLate - keep as string for API
      if (formData.OTDDaysLate !== '') {
        // Check if the value is different from the current value
        const currentValue = selectedJob.OTDDaysLate !== null && selectedJob.OTDDaysLate !== undefined 
          ? selectedJob.OTDDaysLate.toString() 
          : '';
        
        if (formData.OTDDaysLate !== currentValue) {
          patchPayload.OTDDaysLate = formData.OTDDaysLate;
        }
      } else if (selectedJob.OTDDaysLate !== null && selectedJob.OTDDaysLate !== undefined) {
        // If clearing the value (empty string), only update if there was a previous value
        patchPayload.OTDDaysLate = null;
      }
      
      // Handle OTDOverrideReason - combine with Other text if needed
      let newOverrideReason = formData.OTDOverrideReason;
      if (formData.OTDOverrideReason === 'Other' && formData.OTDOverrideReasonOther) {
        newOverrideReason = `Other - ${formData.OTDOverrideReasonOther}`;
      }
      
      if (newOverrideReason !== (selectedJob.OTDOverrideReason || '')) {
        patchPayload.OTDOverrideReason = newOverrideReason;
      }
      
      if (formData.HideFromOTD !== (selectedJob.HideFromOTD || false)) {
        patchPayload.HideFromOTD = formData.HideFromOTD;
      }
      
      // Only make the API call if there are changes (besides the ID)
      if (Object.keys(patchPayload).length === 1) {
        setSaveSuccess(true);
        setTimeout(() => {
          handleCloseModal();
        }, 1500);
        return;
      }
      
      console.log("Making PATCH request to:", `${apiUrls.generalProject}?id=${selectedJob.ID}`);
      console.log("PATCH data:", patchPayload);
      
      await dispatch(patchData({ url: `${apiUrls.generalProject}?id=${selectedJob.ID}`, data: patchPayload }));
      
      // Update local state with only the changed fields (excluding ID)
      const { ID, ...updateFields } = patchPayload;
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.ID === selectedJob.ID 
            ? { ...job, ...updateFields }
            : job
        )
      );
      
      setSaveSuccess(true);
      
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
      
    } catch (err) {
      console.error("Error updating job:", err);
      console.error("API URL:", `${apiUrls.generalProject}?id=${selectedJob.ID}`);
      
      let errorMessage = "Failed to update job. Please try again.";
      if (err.message) {
        errorMessage += ` Error: ${err.message}`;
      }
      
      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };  const getStatusColor = (job) => {
    // Don't apply inline styles for hidden or overridden - let CSS handle cell-specific highlighting
    if (job.HideFromOTD || (job.OTDDaysLate !== null && job.OTDDaysLate !== undefined)) {
      return {};
    }
    
    if (!job.ActualShipDate) return {};
    
    const daysLate = calculateDaysLate(job);
    
    if (daysLate < -1) {
      return { backgroundColor: '#d4edda' }; // Light green for more than 1 day early
    } else if (daysLate === 1) {
      return { backgroundColor: '#fff3cd' }; // Light yellow for exactly 1 day late
    } else if (daysLate > 1) {
      return { backgroundColor: '#f8d7da' }; // Light red for more than 1 day late
    }
    
    return {}; // No highlighting for on-time deliveries or 1 day early
  };
  const renderJobsTable = (jobsList) => {
    return (
      <>        <style>          {`
            /* Cell-specific highlighting for hidden and overridden */
            .otd-table tbody tr[data-status="hidden"] td:nth-child(9) {
              background-color:rgb(217, 35, 50) !important;
            }
            .otd-table tbody tr[data-status="overridden"] td:nth-child(9) {
              background-color: #cce7ff !important;
            }
            /* Full row highlighting for delivery status */
            .otd-table tbody tr[data-status="early"] {
              background-color: #d4edda !important;
            }
            .otd-table tbody tr[data-status="one-day-late"] {
              background-color: #fff3cd !important;
            }
            .otd-table tbody tr[data-status="late"] {
              background-color: #f8d7da !important;
            }
            /* Override Bootstrap striped table styles */
            .otd-table.table-striped tbody tr[data-status]:nth-of-type(odd),
            .otd-table.table-striped tbody tr[data-status]:nth-of-type(even) {
              background-color: inherit !important;
            }
            /* Override Bootstrap striped table styles for specific cells */
            .otd-table.table-striped tbody tr[data-status="hidden"]:nth-of-type(odd) td:nth-child(9),
            .otd-table.table-striped tbody tr[data-status="hidden"]:nth-of-type(even) td:nth-child(9) {
              background-color:rgb(225, 40, 55) !important;
            }
            .otd-table.table-striped tbody tr[data-status="overridden"]:nth-of-type(odd) td:nth-child(9),
            .otd-table.table-striped tbody tr[data-status="overridden"]:nth-of-type(even) td:nth-child(9) {
              background-color: #cce7ff !important;
            }
          `}
        </style>
        <Table striped bordered hover className="otd-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('JobNumber')}>Job Number</th>
              <th onClick={() => handleSort('ModelNumber')}>Model Number</th>
              <th onClick={() => handleSort('ProjectName')}>Project Name</th>
              <th onClick={() => handleSort('ControlPanelExpectedCompletion')}>Control Panel Expected Completion</th>
              <th onClick={() => handleSort('PackDue')}>Pack Due</th>
              <th onClick={() => handleSort('OriginalScheduledShipDate')}>Original Scheduled Ship Date</th>
              <th onClick={() => handleSort('CurrentPlanShipDate')}>Current Plan Ship Date</th>
              <th onClick={() => handleSort('ActualShipDate')}>Actual Ship Date</th>              <th onClick={() => handleSort('DaysLate')}>Days Late</th>
              <th onClick={() => handleSort('ReasonLate')}>Reason Late</th>
              <th onClick={() => handleSort('OTDNotes')}>OTD Notes</th>
            </tr>
          </thead>
          <tbody>
            {jobsList.map((job) => {
              const statusColor = getStatusColor(job);
              let statusType = '';
              
              if (job.HideFromOTD) {
                statusType = 'hidden';
              } else if (job.OTDDaysLate !== null && job.OTDDaysLate !== undefined) {
                statusType = 'overridden';
              } else {
                const daysLate = calculateDaysLate(job);
                if (daysLate < -1) {
                  statusType = 'early';
                } else if (daysLate === 1) {
                  statusType = 'one-day-late';
                } else if (daysLate > 1) {
                  statusType = 'late';
                }
              }

              return (
                <tr 
                  key={job.ID} 
                  onClick={() => handleRowClick(job)}
                  data-status={statusType}
                  style={{ 
                    cursor: 'pointer',
                    ...statusColor
                  }}
                >
                  <td>{job.JobNumber}</td>
                  <td>{job.ModelNumber}</td>
                  <td>{job.ProjectName}</td>
                  <td>{formatDate(job.ControlPanelExpectedCompletion)}</td>
                  <td>{formatDate(job.PackDue)}</td>
                  <td>{formatDate(job.OriginalScheduledShipDate)}</td>
                  <td>{formatDate(job.CurrentPlanShipDate)}</td>
                  <td>{formatDate(job.ActualShipDate)}</td>                  <td>{calculateDaysLate(job) !== null ? calculateDaysLate(job) : 'N/A'}</td>
                  <td>{job.ReasonLate || ''}</td>
                  <td>{job.OTDNotes || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </>
    );
  };

  return (
    <div className="m-4">
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="mb-3 text-center"><h1>On Time Delivery</h1></div>
      
      <Row className="mb-3">
        <Col md={6} lg={4}>          <Form.Control
            type="text"
            placeholder="Search by Job Number, Project Name, or Model Number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2"
          />
        </Col>
        <Col md={6} lg={8}>
          <Row>
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Actual Ship Date Range</Form.Label>
                <div className="d-flex">
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={dateRange.startDate}
                    onChange={handleDateRangeChange}
                    className="me-2"
                  />
                  <span className="align-self-center">to</span>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={dateRange.endDate}
                    onChange={handleDateRangeChange}
                    className="ms-2"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="d-flex flex-wrap">
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2 mb-2"
                  onClick={() => setDateRangePreset(getTwoWeeksAgoRange)}
                >
                  2 Weeks Ago
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2 mb-2"
                  onClick={() => setDateRangePreset(getLastWeekRange)}
                >
                  Last Week
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2 mb-2"
                  onClick={() => setDateRangePreset(getCurrentWeekRange)}
                >
                  Current Week
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2 mb-2"
                  onClick={() => setDateRangePreset(getLast30DaysRange)}
                >
                  Last 30 Days
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2 mb-2"
                  onClick={() => setDateRangePreset(getAllTimeRange)}
                >
                  All Time
                </Button>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : jobs.length === 0 ? (
        <Alert variant="info">No shipped jobs found.</Alert>
      ) : (
        <Tabs defaultActiveKey="stations" className="mb-3">
          <Tab eventKey="stations" title={`Stations (${stationJobs.length})`}>
            {stationJobs.length > 0 ? (
              renderJobsTable(stationJobs)
            ) : (
              <Alert variant="info">No Station jobs found for the selected criteria.</Alert>
            )}
          </Tab>
          <Tab eventKey="mos" title={`MOs (${moJobs.length})`}>
            {moJobs.length > 0 ? (
              renderJobsTable(moJobs)
            ) : (
              <Alert variant="info">No MO jobs found for the selected criteria.</Alert>
            )}
          </Tab>
          <Tab eventKey="other" title={`Other (${otherJobs.length})`}>
            {otherJobs.length > 0 ? (
              renderJobsTable(otherJobs)
            ) : (
              <Alert variant="info">No Other shipped jobs found for the selected criteria.</Alert>
            )}
          </Tab>
        </Tabs>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Delivery Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedJob && (
            <Form>
              <Row className="mb-3 align-items-center">
                <Col>
                  <h5 className="mb-0">
                    {selectedJob.JobNumber} - {selectedJob.ProjectName}
                  </h5>
                </Col>
                <Col xs="auto">
                  <Form.Check
                    type="checkbox"
                    name="HideFromOTD"
                    label="Hide from evaluation"
                    checked={formData.HideFromOTD}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Pack Due</Form.Label>
                    <Form.Control
                      plaintext
                      readOnly
                      value={formatDate(selectedJob.PackDue)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Original Scheduled Ship</Form.Label>
                    <Form.Control
                      plaintext
                      readOnly
                      value={formatDate(selectedJob.OriginalScheduledShipDate)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Actual Ship Date</Form.Label>
                    <Form.Control
                      plaintext
                      readOnly
                      value={formatDate(selectedJob.ActualShipDate)}
                    />
                  </Form.Group>
                </Col>
              </Row>              <Form.Group className="mb-3">
                <Form.Label>Reason Late</Form.Label>
                <Form.Select
                  name="ReasonLate"
                  value={formData.ReasonLate}
                  onChange={handleInputChange}
                >
                  <option value="">Select a reason...</option>
                  {reasonLateOptions.map((option) => (
                    <option key={option.ID} value={option.DisplayText}>
                      {option.DisplayText}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>OTD Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="OTDNotes"
                  value={formData.OTDNotes}
                  onChange={handleInputChange}
                  placeholder="Enter delivery notes"
                />
              </Form.Group>

              <Row className="mb-3">
                <Col md={formData.OTDDaysLate !== '' ? 6 : 12}>
                  <Form.Group>
                    <Form.Label>Days Late (Override)</Form.Label>
                    <Form.Control
                      type="number"
                      name="OTDDaysLate"
                      value={formData.OTDDaysLate}
                      onChange={handleInputChange}
                      placeholder="Enter days late override value"
                    />
                    <Form.Text className="text-muted">
                      Leave blank to use calculated value. 
                      The calculated value is {calculateActualDaysLate(selectedJob) !== null ? calculateActualDaysLate(selectedJob) : 'N/A'}.
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                {formData.OTDDaysLate !== '' && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Override Reason <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="OTDOverrideReason"
                        value={formData.OTDOverrideReason}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a reason...</option>
                        {overrideReasonOptions.map((option) => (
                          <option key={option.ID} value={option.DisplayText}>
                            {option.DisplayText}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>
              
              {formData.OTDOverrideReason === 'Other' && (
                <Form.Group className="mb-3">
                  <Form.Label>Other Reason <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="OTDOverrideReasonOther"
                    value={formData.OTDOverrideReasonOther}
                    onChange={handleInputChange}
                    placeholder="Please specify the reason"
                    required
                  />
                </Form.Group>
              )}
              
              {saveSuccess && (
                <Alert variant="success">
                  Job information updated successfully!
                </Alert>
              )}
              
              {saveError && (
                <Alert variant="danger">
                  {saveError}
                </Alert>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveChanges}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default OnTimeDelivery;
